import { flash, FormGroup, Label, SubmitButton, Switch } from "@xola/ui-kit";
import { AxiosError } from "axios";
import { FormField, InputField, requiredString } from "common";
import { noop } from "lodash";
import React, { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { AbandonedPreference, Channel, NotificationPreferenceType } from "types";
import { boolean, object, string } from "yup";
import { RichEditor } from "../Common/RichEditor";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { PreviewMessageTemplateDrawer } from "../Customers/Templates/PreviewMessageTemplateDrawer";
import { ContentLayout } from "../Layout/ContentLayout";
import { AbandonedBookingReminderPreferenceModal } from "../Settings/Notifications/AbandonedBookingReminderPreference";
import {
    NotificationPreferenceListGroup,
    useNotificationPreferenceList,
} from "../Settings/Notifications/useNotificationPreferenceList";
import { api } from "@/api/api";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useSeller } from "@/hooks/useAccount";
import { useVisibility } from "@/hooks/useVisibility";

const validationSchema = object().shape({
    replyToEmail: string().email(),
    enabled: boolean().required(),
    template: requiredString(),
});

const initialValues: AbandonedPreference = {
    enabled: true,
    overrideTemplate: false,
    emailSubject: "",
    senderName: "",
    replyToEmail: "",
    template: "",
};

export const AbandonedBookingRecovery = () => {
    const seller = useSeller();

    const [templatePreview, setTemplatePreview] = useState<string | null>(null);

    const { data: preview } = usePreviewTemplate(templatePreview);

    const [visible, { toggle }] = useVisibility(false);
    const [initialTemplate, setInitialTemplate] = useState(
        seller.preferences.abandoned.overrideTemplate
            ? seller.preferences.abandoned.template ?? defaultTemplate
            : defaultTemplate,
    );
    const [inputKey, setInputKey] = useState(0);

    const { mutateAsync: savePreference, isLoading } = useMutation(async (preference: AbandonedPreference) => {
        return api.post(`/sellers/${seller.id}/preferences/abandoned`, preference);
    });

    const notificationPreferences = useNotificationPreferenceList(noop);
    const abandonedTimingItem = notificationPreferences.list.traveler.preferences.find(
        (preference) => preference.type === NotificationPreferenceType.ABANDONED_RECOVERY_TRAVELER,
    );

    const handleAbandonedTimingApply = (payload: {
        channels: Channel[];
        sendStartTime: number;
        sendEndTime: number;
    }) => {
        notificationPreferences.update(
            NotificationPreferenceListGroup.TRAVELER,
            NotificationPreferenceType.ABANDONED_RECOVERY_TRAVELER,
            (draft) => {
                draft.channels = payload.channels;
                draft.sendStartTime = payload.sendStartTime;
                draft.sendEndTime = payload.sendEndTime;
            },
        );
    };

    const handleSubmit = async (values: AbandonedPreference) => {
        await Promise.all([savePreference(values), notificationPreferences.save()]);
        flash.success("Abandoned recovery email content saved");
    };

    const handleCancel = (reset: () => void) => {
        reset();
        notificationPreferences.reset();
        setInputKey(inputKey + 1);
    };

    return (
        <XolaFormik
            validationSchema={validationSchema}
            initialValues={{ ...initialValues, ...seller.preferences.abandoned, template: initialTemplate }}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, handleReset, values, setFieldValue, setValues, isValid }) => {
                const handleResetContent = () => {
                    setValues({ ...initialValues, template: defaultTemplate });
                    setInitialTemplate(defaultTemplate);
                };

                return (
                    <ContentLayout isWide className="space-y-6">
                        <ContentLayout.Section
                            title="Abandoned Reservation Recovery"
                            description="Abandoned reservation recovery is an automated re-marketing that engages customers who started making a purchase through your checkout but did not complete the purchase"
                        >
                            <div className="space-y-2 rounded bg-gray-lighter py-4 px-6">
                                <Switch.Group>
                                    <Switch
                                        isChecked={values.enabled}
                                        size="medium"
                                        onChange={(value: boolean) => setFieldValue("enabled", value)}
                                    />

                                    <Switch.Label direction="left" className="ml-3 font-bold">
                                        {values.enabled ? "Enabled" : "Disabled"}
                                    </Switch.Label>
                                </Switch.Group>

                                <div>
                                    Automated emails are{" "}
                                    {values.enabled ? "active and being sent." : "inactive and not being sent."}
                                </div>
                            </div>
                        </ContentLayout.Section>

                        <ContentLayout.Section
                            title="Email Content"
                            description={<EmailContentDescription onClick={handleResetContent} />}
                        >
                            <div>
                                <FormField name="emailSubject" label="Subject">
                                    <InputField placeholder={defaultEmailSubject} />
                                </FormField>

                                <FormField name="senderName" label="From Name">
                                    <InputField placeholder={seller.name} />
                                </FormField>

                                <FormField name="replyToEmail" label="Reply to Email">
                                    <InputField placeholder={seller.email} />
                                </FormField>

                                <FormField name="template" label="Body">
                                    <RichEditor
                                        key={inputKey}
                                        initialContent={values.template}
                                        defaultTemplate={defaultTemplate}
                                        onChange={(html) => {
                                            setFieldValue("template", html);
                                            setFieldValue("overrideTemplate", true);
                                        }}
                                    />
                                </FormField>
                            </div>

                            <SecondaryButton
                                className="mr-auto"
                                size="small"
                                onClick={() => {
                                    if (isValid) {
                                        setTemplatePreview(values.template);
                                        toggle();
                                    } else {
                                        handleSubmit(); // hit handleSubmit to show errors.
                                    }
                                }}
                            >
                                Preview
                            </SecondaryButton>

                            <PreviewMessageTemplateDrawer isOpen={visible} onClose={toggle}>
                                {preview ? (
                                    <iframe sandbox="allow-same-origin" srcDoc={preview} className="h-full w-full" />
                                ) : null}
                            </PreviewMessageTemplateDrawer>
                        </ContentLayout.Section>

                        {abandonedTimingItem && (
                            <ContentLayout.Section
                                title="Timing"
                                description="The duration of time the abandoned reservation recovery email is sent after reservation abandonment"
                            >
                                <FormGroup>
                                    <Label>Timing Delay</Label>
                                    <span>
                                        <AbandonedBookingReminderPreferenceModal
                                            item={abandonedTimingItem}
                                            onApply={handleAbandonedTimingApply}
                                        />
                                    </span>
                                </FormGroup>
                            </ContentLayout.Section>
                        )}

                        <ContentLayout.Footer>
                            <SubmitButton type="submit" isLoading={isLoading} onClick={handleSubmit}>
                                Save
                            </SubmitButton>

                            <SecondaryButton type="reset" onClick={() => handleCancel(handleReset)}>
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </ContentLayout>
                );
            }}
        </XolaFormik>
    );
};

const defaultEmailSubject = "Need help with your purchase?";

const defaultTemplate = `<p>Hi {{purchase.customerName|first_name}},</p>
<p>I saw you started to reserve {{purchase.items[0].product.name}} for {{'EMAIL.ABANDON.PEOPLE'|transchoice(purchase.items[0].quantity)}}, but weren't able to complete it. If you are still interested, <a href="{{ website }}{{ resumeUrl }}" target="_blank">Resume Booking</a> now.</p>
<p>If you have any questions, shoot us a quick note or give us a call at {{seller.phone}}. Let us know how we can help. We'd love to make this work for you.</p>
<p>Best wishes,</p>
<p>{{seller.name}}</p>
<p>{{seller.website}}</p>
`;

const usePreviewTemplate = (template: string | null) => {
    const seller = useSeller();

    // insert styles for blockquote
    return useQuery<any, AxiosError, string>(
        ["preview", "abandoned", template],
        async () => {
            const { data } = await api.post<string>(`/sellers/${seller.id}/abandoned/preview`, { template });
            return insertStyles(data);
        },
        { staleTime: 0, keepPreviousData: true, enabled: !!template },
    );
};

const insertStyles = (html: string) => {
    const start = html.indexOf("<head>");
    if (start >= 0) {
        const style =
            "<style>blockquote {padding: 0 0 0 15px;margin: 0 0 18px;border-left: 5px solid #f2f2f2;}</style>";
        return html.slice(0, start + 6) + style + html.slice(start + 6);
    }

    return html;
};

const EmailContentDescription = ({ onClick }: { onClick: () => void }) => {
    return (
        <div>
            The email sent to re-engage a customer after cart abandonment
            <div className="mt-2">
                <SecondaryButton size="small" onClick={onClick}>
                    Reset to Default Content
                </SecondaryButton>
            </div>
        </div>
    );
};
